import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { chatAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Send, ArrowLeft, Users, MessageCircle, Lock, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const C = {
  bg: '#0A0F1E', surface: '#111827', card: '#1A2236',
  gold: '#D4A853', cream: '#F8F5EE', muted: '#94A3B8',
};

// ==================== CHAT ROOM LIST ====================

const ChatRoomList = ({ onSelectRoom }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['chat-rooms'],
    queryFn: async () => (await chatAPI.getMyRooms()).data,
    refetchInterval: 15000,
  });

  if (isLoading) return (
    <div className="text-center py-12">
      <div className="animate-spin w-6 h-6 border-3 border-indigo-500 border-t-transparent rounded-full mx-auto mb-2" />
      <p className="text-xs font-bold" style={{ color: C.muted }}>Loading chats...</p>
    </div>
  );

  const { rooms = [], chat_globally_disabled, chat_parent_disabled } = data || {};

  if (chat_globally_disabled) {
    return (
      <div className="text-center py-12 px-4">
        <Lock size={40} className="mx-auto mb-3" style={{ color: C.muted }} />
        <p className="font-bold text-sm" style={{ color: C.cream }}>Chat is currently disabled</p>
        <p className="text-xs mt-1" style={{ color: C.muted }}>The administrator has turned off the chat system.</p>
      </div>
    );
  }

  if (chat_parent_disabled) {
    return (
      <div className="text-center py-12 px-4">
        <Lock size={40} className="mx-auto mb-3" style={{ color: C.muted }} />
        <p className="font-bold text-sm" style={{ color: C.cream }}>Chat is disabled by your parent</p>
        <p className="text-xs mt-1" style={{ color: C.muted }}>Ask your parent to enable classroom chat in Reading Rules.</p>
      </div>
    );
  }

  if (rooms.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <MessageCircle size={40} className="mx-auto mb-3" style={{ color: C.muted }} />
        <p className="font-bold text-sm" style={{ color: C.cream }}>No active chats</p>
        <p className="text-xs mt-1" style={{ color: C.muted }}>Join a classroom and ask your teacher to enable chat.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 p-3">
      <p className="text-[10px] font-bold uppercase px-1 mb-2" style={{ color: C.muted }}>Your Classrooms</p>
      {rooms.map(room => (
        <button key={room.classroom_id} onClick={() => onSelectRoom(room)}
          className="w-full text-left p-3 rounded-xl transition-all hover:scale-[1.01]"
          style={{ background: C.card, border: '1px solid rgba(255,255,255,0.06)' }}
          data-testid={`chat-room-${room.classroom_id}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(99,102,241,0.15)' }}>
              <Users size={18} style={{ color: '#818CF8' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate" style={{ color: C.cream }}>{room.classroom_name}</p>
              {room.last_message ? (
                <p className="text-xs truncate" style={{ color: C.muted }}>
                  <span className="font-semibold">{room.last_message_by}:</span> {room.last_message}
                </p>
              ) : (
                <p className="text-xs" style={{ color: C.muted }}>No messages yet</p>
              )}
            </div>
            {room.last_message_date && (
              <span className="text-[10px] flex-shrink-0" style={{ color: C.muted }}>
                {formatTimeAgo(room.last_message_date)}
              </span>
            )}
          </div>
        </button>
      ))}
    </div>
  );
};

// ==================== CHAT ROOM VIEW ====================

const ChatRoom = ({ room, onBack }) => {
  const { student } = useAuth();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const studentId = student?.id;

  const { data: messages = [] } = useQuery({
    queryKey: ['chat-messages', room.classroom_id],
    queryFn: async () => (await chatAPI.getMessages(room.classroom_id)).data,
    refetchInterval: 3000,  // Poll every 3 seconds for new messages
  });

  const { data: status } = useQuery({
    queryKey: ['chat-status', room.classroom_id],
    queryFn: async () => (await chatAPI.getStatus(room.classroom_id)).data,
  });

  const sendMut = useMutation({
    mutationFn: (data) => chatAPI.sendMessage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages', room.classroom_id] });
      setMessage('');
      inputRef.current?.focus();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to send'),
  });

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    sendMut.mutate({ classroom_id: room.classroom_id, message: message.trim() });
  };

  const members = status?.members || [];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <button onClick={onBack} className="p-1.5 rounded-lg transition-all hover:bg-white/5">
          <ArrowLeft size={18} style={{ color: C.muted }} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate" style={{ color: C.cream }}>{room.classroom_name}</p>
          <p className="text-[10px]" style={{ color: C.muted }}>{members.length} members</p>
        </div>
        <button onClick={() => setShowMembers(!showMembers)}
          className="p-1.5 rounded-lg transition-all hover:bg-white/5"
          style={{ color: showMembers ? '#818CF8' : C.muted }}>
          <Users size={18} />
        </button>
      </div>

      {/* Members dropdown */}
      {showMembers && (
        <div className="px-3 py-2" style={{ background: 'rgba(99,102,241,0.05)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex flex-wrap gap-2">
            {members.map(m => (
              <span key={m.id} className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-bold"
                style={{
                  background: m.id === studentId ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.05)',
                  color: m.id === studentId ? '#818CF8' : C.muted,
                }}>
                {m.photo ? (
                  <img src={`${API_BASE}${m.photo}`} alt="" className="w-4 h-4 rounded-full" />
                ) : (
                  <div className="w-4 h-4 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }} />
                )}
                {m.name} {m.id === studentId && '(you)'}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2" style={{ minHeight: 0 }}>
        {messages.length === 0 && (
          <div className="text-center py-8">
            <MessageCircle size={32} className="mx-auto mb-2" style={{ color: 'rgba(255,255,255,0.1)' }} />
            <p className="text-xs" style={{ color: C.muted }}>No messages yet. Say hi!</p>
          </div>
        )}
        {messages.map((msg, idx) => {
          const isMe = msg.student_id === studentId;
          const showName = !isMe && (idx === 0 || messages[idx - 1]?.student_id !== msg.student_id);

          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
                {showName && (
                  <p className="text-[10px] font-bold mb-0.5 px-2" style={{ color: '#818CF8' }}>
                    {msg.student_name}
                  </p>
                )}
                <div className="px-3 py-2 rounded-2xl text-sm"
                  style={{
                    background: isMe ? '#4F46E5' : 'rgba(255,255,255,0.08)',
                    color: isMe ? 'white' : C.cream,
                    borderBottomRightRadius: isMe ? '4px' : '16px',
                    borderBottomLeftRadius: isMe ? '16px' : '4px',
                  }}>
                  {msg.message}
                </div>
                <p className={`text-[9px] mt-0.5 px-2 ${isMe ? 'text-right' : ''}`} style={{ color: 'rgba(255,255,255,0.2)' }}>
                  {formatTime(msg.created_date)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-3 flex gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <input ref={inputRef} type="text" value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder="Type a message..."
          maxLength={500}
          className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium outline-none"
          style={{ background: 'rgba(255,255,255,0.06)', color: C.cream, border: '1px solid rgba(255,255,255,0.1)' }}
          data-testid="chat-input" />
        <button type="submit" disabled={!message.trim() || sendMut.isPending}
          className="w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-30"
          style={{ background: message.trim() ? '#4F46E5' : 'rgba(255,255,255,0.06)' }}
          data-testid="chat-send-btn">
          <Send size={16} style={{ color: message.trim() ? 'white' : C.muted }} />
        </button>
      </form>
    </div>
  );
};

// ==================== MAIN CHAT PANEL ====================

const ChatPanel = ({ isOpen, onClose }) => {
  const [selectedRoom, setSelectedRoom] = useState(null);

  if (!isOpen) return null;

  return (
    <div className="fixed top-20 right-4 z-50 w-[340px] sm:w-[380px] rounded-2xl overflow-hidden shadow-2xl"
      style={{ background: C.surface, border: '1px solid rgba(255,255,255,0.08)', height: '500px', maxHeight: 'calc(100vh - 100px)' }}
      data-testid="chat-panel">
      {/* Panel Header */}
      <div className="flex items-center justify-between px-4 py-3"
        style={{ background: C.bg, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-2">
          <MessageCircle size={18} style={{ color: '#818CF8' }} />
          <p className="text-sm font-black" style={{ color: C.cream }}>
            {selectedRoom ? selectedRoom.classroom_name : 'Classroom Chat'}
          </p>
        </div>
        <button onClick={() => { onClose(); setSelectedRoom(null); }}
          className="text-xs font-bold px-2 py-1 rounded-lg transition-all hover:bg-white/5"
          style={{ color: C.muted }}>
          Close
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-col" style={{ height: 'calc(100% - 48px)' }}>
        {selectedRoom ? (
          <ChatRoom room={selectedRoom} onBack={() => setSelectedRoom(null)} />
        ) : (
          <ChatRoomList onSelectRoom={setSelectedRoom} />
        )}
      </div>
    </div>
  );
};

// ==================== CHAT BUTTON (FLOATING) ====================

export const ChatButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { student } = useAuth();

  if (!student) return null;

  return (
    <>
      <button onClick={() => setIsOpen(!isOpen)}
        className="fixed top-20 right-4 z-40 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
        style={{
          background: isOpen ? '#4F46E5' : 'linear-gradient(135deg, #4F46E5, #7C3AED)',
          display: isOpen ? 'none' : 'flex',
        }}
        data-testid="chat-toggle-btn">
        <MessageCircle size={22} className="text-white" />
      </button>
      <ChatPanel isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

// ==================== HELPERS ====================

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default ChatPanel;
